// Licence register screen model (0021): filters, labels and the record-a-licence form.
// Pure functions only, so the unit tests cover them without a browser. The server decides
// everything: these helpers only shape what is shown and what is sent.
// Licences are role-based and company-wide (HSE Manager decision, 4 November 2026).
import { LICENCE_ROLES } from '@/domain/catalogue'
import type { CommandArgs, LicenceRegister, LicenceStatus } from '@/domain/types'
import type { Tone } from '@/components/ui'

export type Licence = LicenceRegister['licences'][number]

export type LicenceFilter = { person: string; role: string; status: LicenceStatus | '' }

export const EMPTY_FILTER: LicenceFilter = { person: '', role: '', status: '' }

export const STATUS_LABEL: Record<LicenceStatus, string> = {
  current: 'Current',
  expired: 'Expired',
  revoked: 'Withdrawn',
  not_yet_valid: 'Not yet valid',
  expiry_not_stated: 'No stated expiry (not accepted)',
  unverified: 'Not verified',
  missing: 'Missing',
}

export const STATUS_TONE: Record<LicenceStatus, Tone> = {
  current: 'ok',
  expired: 'bad',
  revoked: 'bad',
  not_yet_valid: 'warn',
  expiry_not_stated: 'warn',
  unverified: 'warn',
  missing: 'bad',
}

export const KIND_LABEL: Record<string, string> = {
  ptw_licence: 'PTW licence',
  agt_certificate: 'Gas tester certificate',
  appointment: 'Appointment',
}

/** The roles the register can record, in catalogue order. */
export const RECORDABLE_ROLES: readonly string[] = LICENCE_ROLES.map((r) => r.code)

export function filterLicences(list: readonly Licence[], f: LicenceFilter): Licence[] {
  return list.filter((l) => (!f.person || l.person_id === f.person) && (!f.role || l.role_code === f.role) && (!f.status || l.status === f.status))
}

/** Role codes present in the register, for the role filter. */
export function roleFilterOptions(list: readonly Licence[]): string[] {
  const present = new Set(list.map((l) => l.role_code))
  const order = [...RECORDABLE_ROLES, ...[...present].filter((r) => !RECORDABLE_ROLES.includes(r)).sort()]
  return order.filter((r) => present.has(r))
}

/**
 * Where a licence is valid, in words. Company-wide licences are valid at every BadrEOR
 * location; an older licence recorded for named units only says which units.
 */
export function scopeSummary(l: Pick<Licence, 'company_wide' | 'scopes'>): string {
  if (l.company_wide) return 'Every BadrEOR location'
  const units = l.scopes
    .filter((s) => s.site_area)
    .map((s) => (s.work_type_code ? `${s.site_area!.name} (work type ${s.work_type_code} only)` : s.site_area!.name))
  return units.length > 0 ? `Named units only (older record): ${units.join(', ')}` : 'No scope on your sites'
}

/** A licence the administrator may withdraw now: not their own and not already withdrawn from now. */
export function canWithdraw(l: Pick<Licence, 'is_mine' | 'status' | 'withdrawal'>, nowIso: string): boolean {
  if (l.is_mine || l.status === 'revoked') return false
  return !(l.withdrawal && Date.parse(l.withdrawal.effective_at) <= Date.parse(nowIso))
}

/** "Name (deputies: A, B)" for a unit custodian, or the missing-custodian text. */
export function custodianText(c: { responsible: { display_name: string }; deputies: { display_name: string }[] } | null): string {
  if (!c) return 'No responsible RS named: no agreement can be given for this unit'
  return c.deputies.length > 0 ? `${c.responsible.display_name} (deputies: ${c.deputies.map((d) => d.display_name).join(', ')})` : c.responsible.display_name
}

export type RecordForm = {
  person_id: string
  role_code: string
  issued_on: string
  expires_on: string
  expiry_not_stated: boolean
  evidence_reference: string
  issuer: string
}

export function emptyRecordForm(): RecordForm {
  return { person_id: '', role_code: '', issued_on: '', expires_on: '', expiry_not_stated: false, evidence_reference: '', issuer: '' }
}

/** The command arguments for a filled form; empty answers are sent so the server lists every missing one. */
export function recordArgs(f: RecordForm): CommandArgs['register_credential_evidence'] {
  return {
    person_id: f.person_id,
    role_code: f.role_code,
    issued_on: f.issued_on,
    expires_on: f.expiry_not_stated ? null : f.expires_on || null,
    expiry_not_stated: f.expiry_not_stated,
    evidence_reference: f.evidence_reference,
    issuer: f.issuer.trim() ? f.issuer : null,
  }
}
